import React, { useMemo } from "react";
import * as THREE from "three";
import { Line } from "@react-three/drei";
import { normalizeAxis, toNumber } from "./mountingDrillUtils";

export default function DrillingPreviewMesh({
  drill,
  selected = false,
  onClick,
}) {
  const diameter = toNumber(drill?.diameter, 5);
  const depth = toNumber(drill?.depth, 10);

  const start = useMemo(
    () =>
      new THREE.Vector3(
        toNumber(drill?.start?.x, 0),
        toNumber(drill?.start?.y, 0),
        toNumber(drill?.start?.z, 0)
      ),
    [drill]
  );

  const axis = useMemo(() => normalizeAxis(drill?.axis), [drill]);

  const end = useMemo(
    () => start.clone().add(axis.clone().multiplyScalar(depth)),
    [start, axis, depth]
  );

  const center = useMemo(
    () => start.clone().add(end).multiplyScalar(0.5),
    [start, end]
  );

  const quaternion = useMemo(() => {
    const q = new THREE.Quaternion();
    q.setFromUnitVectors(
      new THREE.Vector3(0, 1, 0),
      axis.clone().normalize()
    );
    return q;
  }, [axis]);

  const color = selected ? "#f59e0b" : drill?.color || "#3b82f6";

  return (
    <group onClick={(e) => {
      e.stopPropagation();
      onClick?.(drill);
    }}>
      <Line
        points={[
          [start.x, start.y, start.z],
          [end.x, end.y, end.z],
        ]}
        color={color}
        lineWidth={2}
      />

      <mesh position={[start.x, start.y, start.z]}>
        <sphereGeometry args={[1.0, 12, 12]} />
        <meshStandardMaterial color={color} />
      </mesh>

      <mesh
        position={[center.x, center.y, center.z]}
        quaternion={quaternion}
      >
        <cylinderGeometry args={[diameter / 2, diameter / 2, depth, 24]} />
        <meshStandardMaterial
          color={color}
          transparent
          opacity={0.25}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}